import { Component, Input, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BookingConfiguredData } from '../booking-config.model';
import { DealDto } from '@app/model/deal.model';
import { createDealInfo, isBookingDataValid } from '../booking-config.utils';
import { GeneralErrorService } from '@app/components/general-error/general-error.service';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { UserService } from '@app/services/user.service';
import { CheckoutGuest } from '@app/user/checkout/checkout.model';
import { EventService, EventType } from '@app/utilities/event.service';
import { getBookingUrl } from '@app/utilities/url.utils';
import { CurrencyLanguageService } from '@app/shared/currency-language.service';
import { AuthHttpClientNoXService } from '@app/services/auth-http-client-noX.service';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
    selector: 'md-travel',
    templateUrl: './travel.component.html',
    styleUrls: ['./travel.component.scss'],
})
export class TravelComponent {
    @Input() bookingConfiguredData: BookingConfiguredData;
    @Input() deal: DealDto;

    @ViewChild('confirmationModal')
    confirmationModal: TemplateRef<unknown>;
    @ViewChild('successModal') successModal: TemplateRef<unknown>;

    constructor(
        private modalService: NgbModal,
        private generalErrorService: GeneralErrorService,
        private translate: TranslateService,
        private userService: UserService,
        private eventService: EventService,
        private currencyLanguageService: CurrencyLanguageService,
        private authHttpClient: AuthHttpClientNoXService,
        private spinner: NgxSpinnerService
    ) {}

    openOnRequestConfirmationModal(): void {
        if (this.userService.isLoggedIn) {
            if (
                !isBookingDataValid(
                    this.bookingConfiguredData.productBookingData
                )
            ) {
                this.generalErrorService.showGeneralError(
                    this.translate.instant(
                        'Invalid selection. Please check occupancy and dates and try again.'
                    ),
                    { showMailto: false, showImage: false }
                );
                return;
            }

            this.modalService.open(this.confirmationModal, {
                centered: true,
                size: 'md',
                backdrop: 'static',
                keyboard: false,
            });
        } else {
            this.eventService.emitEvent(
                EventType.LOGIN,
                window.location.pathname
            );
        }
    }

    confirmBookingRequest(): void {
        const guestData: CheckoutGuest = this.createGuestListForItem();
        this.modalService.dismissAll();
        this.spinner.show();

        this.placeTravelBookingRequest(
            guestData,
            this.deal,
            this.bookingConfiguredData
        ).subscribe({
            next: () => {
                this.bookingConfiguredData = {
                    ...this.bookingConfiguredData,
                    totalPrice: 0,
                    totalFullPrice: 0,
                };
                this.modalService.open(this.successModal, {
                    centered: true,
                    size: 'md',
                    backdrop: 'static',
                    keyboard: false,
                });
                this.spinner.hide();
                // the cart is deleted when placing a travel deal so we need to delete it from frontend too
                this.userService.deleteLocalCart();
            },
            error: (error) => {
                this.generalErrorService.showGeneralError(
                    this.translate.instant(
                        'An error occurred while placing the booking request. Please try again later.'
                    ),
                    { showMailto: true, showImage: false }
                );
                this.spinner.hide();
                // the cart is deleted when placing a travel deal so we need to delete it from frontend too
                this.userService.deleteLocalCart();
            },
        });
    }

    private createGuestListForItem() {
        const { userName, email } = this.userService.getUserDataImmediate();

        return {
            name: userName,
            email: email,
            categoryproductId: '18', // travel category product ID
            guestrequest: '',
            dealId: this.deal.dealId,
            product_name: this.deal.dealTitle,
            guest_id: `${this.deal.dealId}-1`,
            remove_price: false,
            room_count: 1,
            child_range: [],
        };
    }

    private placeTravelBookingRequest(
        guestData: CheckoutGuest,
        deal: DealDto,
        bookingConfiguredData: BookingConfiguredData
    ): Observable<unknown> {
        const url = getBookingUrl() + 'orders';

        const apiUrl = `${getBookingUrl()}orders/travel`;

        const { totalPrice, totalFullPrice } = bookingConfiguredData;

        const { currencyId, country, customerGroupId } =
            this.currencyLanguageService.getLocaleDataImmediate();

        const cartItem = {
            outletId: deal.outletId,
            dealId: deal.dealId,
            dealCategoryId: deal.categoryId,
            sectionId: deal.categoryId,
            dealTitle: deal.dealTitle,
            dealSubtitle: deal.subTitle,
            rewardPointsEarned: 0,
            quantity: 1,
            subtotal: totalPrice,
            purchaseCurrencyId: currencyId,
            grandTotal: totalFullPrice,
            dealInfo: createDealInfo(
                bookingConfiguredData.productBookingData,
                deal
            ),
        };

        const postBody = {
            paymentMethod: '18',
            guestData: [
                {
                    dealId: guestData.dealId,
                    name: guestData.name,
                    email: guestData.email,
                    specialRequest: guestData.guestrequest,
                    hidePrice: guestData.remove_price,
                },
            ],
            cartItem,
        };

        const queryParams = new URLSearchParams({
            orgId: '1',
            countryCode: country,
            customerGroupId: customerGroupId?.toString?.(),
        });

        return this.authHttpClient.post<unknown, unknown>(
            `${apiUrl}?${queryParams.toString()}`,
            postBody
        );
    }
}
